import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import ContestantHeader from '../../components/ContestantHeader';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import './contestant.css';

// ── Reusable password input with Show/Hide toggle ─────────────────────────────
function PwInput({ id, value, onChange, autoComplete, label }) {
  const [visible, setVisible] = useState(false);
  return (
    <div className="c-form-group">
      <label className="c-label" htmlFor={id}>{label}</label>
      <div className="c-pw-wrapper">
        <input
          id={id}
          type={visible ? 'text' : 'password'}
          className="c-input"
          value={value}
          onChange={onChange}
          autoComplete={autoComplete}
        />
        <button
          type="button"
          className="c-pw-toggle"
          onClick={() => setVisible((v) => !v)}
          tabIndex={-1}
        >
          {visible ? 'Hide' : 'Show'}
        </button>
      </div>
    </div>
  );
}

// ── Inline alert banner ───────────────────────────────────────────────────────
function Alert({ msg }) {
  if (!msg) return null;
  return (
    <div
      className={`c-alert${msg.type === 'success' ? ' c-alert--success' : ''}`}
      role="alert"
    >
      {msg.text}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function ContestantProfile() {
  const { updateUser } = useAuth();

  // ── Personal info state ─────────────────────────────────────────────────────
  const [name,          setName]          = useState('');
  const [phone,         setPhone]         = useState('');
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileMsg,    setProfileMsg]    = useState(null);

  // ── Password state ──────────────────────────────────────────────────────────
  const [currentPw,  setCurrentPw]  = useState('');
  const [newPw,      setNewPw]      = useState('');
  const [confirmPw,  setConfirmPw]  = useState('');
  const [pwLoading,  setPwLoading]  = useState(false);
  const [pwMsg,      setPwMsg]      = useState(null);

  // ── Membership state ─────────────────────────────────────────────────────────
  const [isMember,      setIsMember]      = useState(false);
  const [memberLoading, setMemberLoading] = useState(false);
  const [memberMsg,     setMemberMsg]     = useState(null);

  // Load existing profile data on mount
  useEffect(() => {
    api.get('/api/contestants/me')
      .then((res) => {
        const c = res.data.contestant;
        if (c) {
          setName(c.full_name  ?? '');
          setPhone(c.phone     ?? '');
          setIsMember(c.is_member ?? false);
        } else {
          // No contestants profile yet — show membership from users table
          setIsMember(res.data.user_is_member ?? false);
        }
      })
      .catch(() => {
        // Silently ignore — form will just be empty
      });
  }, []);

  // ── Personal info submit ────────────────────────────────────────────────────
  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      setProfileMsg({ type: 'error', text: 'Full name is required.' });
      return;
    }
    setProfileLoading(true);
    setProfileMsg(null);
    try {
      const res = await api.put('/api/contestants/me/profile', {
        full_name: name.trim(),
        phone:     phone.trim() || null,
      });
      // Reflect updated name in the header immediately
      updateUser({ full_name: res.data.full_name });
      setName(res.data.full_name);
      setPhone(res.data.phone ?? '');
      setProfileMsg({ type: 'success', text: 'Profile updated successfully.' });
    } catch (err) {
      setProfileMsg({
        type: 'error',
        text: err.response?.data?.error ?? 'Failed to update profile.',
      });
    } finally {
      setProfileLoading(false);
    }
  };

  // ── Membership submit ────────────────────────────────────────────────────────
  const handleMembershipChange = async (newValue) => {
    setMemberLoading(true);
    setMemberMsg(null);
    try {
      const res = await api.put('/api/contestants/me/membership', { is_member: newValue });
      setIsMember(res.data.is_member);
      setMemberMsg({ type: 'success', text: 'Membership status updated.' });
    } catch (err) {
      setMemberMsg({
        type: 'error',
        text: err.response?.data?.error ?? 'Failed to update membership status.',
      });
    } finally {
      setMemberLoading(false);
    }
  };

  // ── Password submit ─────────────────────────────────────────────────────────
  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (!currentPw || !newPw || !confirmPw) {
      setPwMsg({ type: 'error', text: 'All password fields are required.' });
      return;
    }
    if (newPw.length < 8) {
      setPwMsg({ type: 'error', text: 'New password must be at least 8 characters.' });
      return;
    }
    if (newPw !== confirmPw) {
      setPwMsg({ type: 'error', text: 'New passwords do not match.' });
      return;
    }
    setPwLoading(true);
    setPwMsg(null);
    try {
      const res = await api.put('/api/contestants/me/password', {
        current_password: currentPw,
        new_password:     newPw,
      });
      setPwMsg({ type: 'success', text: res.data.message });
      setCurrentPw('');
      setNewPw('');
      setConfirmPw('');
    } catch (err) {
      setPwMsg({
        type: 'error',
        text: err.response?.data?.error ?? 'Failed to change password.',
      });
    } finally {
      setPwLoading(false);
    }
  };

  return (
    <div className="c-page">
      <ContestantHeader />

      <main className="c-main">

        {/* ── Page title ── */}
        <div className="c-page-title-row">
          <div>
            <h1 className="c-page-title">My Profile</h1>
            <p className="c-page-subtitle">Update your personal information and password</p>
          </div>
          <Link to="/contestant/dashboard" className="c-back-btn" style={{ marginBottom: 0 }}>
            ← Back to Dashboard
          </Link>
        </div>

        {/* ── Personal information ── */}
        <div className="c-form-card">
          <h2 className="c-form-card__title">Personal Information</h2>

          <Alert msg={profileMsg} />

          <form onSubmit={handleProfileSubmit} noValidate>
            <div className="c-form-group">
              <label className="c-label" htmlFor="profile-name">
                Full name <span className="c-label__required">*</span>
              </label>
              <input
                id="profile-name"
                type="text"
                className="c-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={120}
                autoComplete="name"
              />
            </div>

            <div className="c-form-group">
              <label className="c-label" htmlFor="profile-phone">
                Phone number{' '}
                <span className="c-label-optional">(optional)</span>
              </label>
              <input
                id="profile-phone"
                type="tel"
                className="c-input"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                maxLength={30}
                placeholder="e.g. 0412 345 678"
                autoComplete="tel"
              />
            </div>

            <button
              type="submit"
              className="c-btn"
              disabled={profileLoading}
              style={{ marginTop: '0.25rem' }}
            >
              {profileLoading ? (
                <>
                  <span className="c-spinner c-spinner--sm c-spinner--white" aria-hidden="true" />
                  Saving…
                </>
              ) : (
                'Save changes'
              )}
            </button>
          </form>
        </div>

        {/* ── Change password ── */}
        <div className="c-form-card">
          <h2 className="c-form-card__title">Change Password</h2>

          <Alert msg={pwMsg} />

          <form onSubmit={handlePasswordSubmit} noValidate>
            <PwInput
              id="current-pw"
              label="Current password"
              value={currentPw}
              onChange={(e) => setCurrentPw(e.target.value)}
              autoComplete="current-password"
            />

            <PwInput
              id="new-pw"
              label="New password"
              value={newPw}
              onChange={(e) => setNewPw(e.target.value)}
              autoComplete="new-password"
            />
            <p className="c-field-hint" style={{ marginTop: '-0.65rem', marginBottom: '1.1rem' }}>
              Minimum 8 characters.
            </p>

            <PwInput
              id="confirm-pw"
              label="Confirm new password"
              value={confirmPw}
              onChange={(e) => setConfirmPw(e.target.value)}
              autoComplete="new-password"
            />

            <button
              type="submit"
              className="c-btn"
              disabled={pwLoading}
              style={{ marginTop: '0.25rem' }}
            >
              {pwLoading ? (
                <>
                  <span className="c-spinner c-spinner--sm c-spinner--white" aria-hidden="true" />
                  Updating…
                </>
              ) : (
                'Change password'
              )}
            </button>
          </form>
        </div>

        {/* ── SCALA Membership ── */}
        <div className="c-form-card">
          <h2 className="c-form-card__title">SCALA Membership Status</h2>

          <p
            style={{
              marginTop: '0.75rem',
              marginBottom: '1rem',
              color: '#6b7280',
              fontSize: '0.9rem',
            }}
          >
            Check your SCALA Membership status{' '}
            <a
              href="https://members.scala.org.au/login"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#2563eb', textDecoration: 'underline' }}
            >
              here
            </a>
            .
          </p>

          <Alert msg={memberMsg} />

          <p style={{ color: '#6b7280', fontSize: '0.9rem', marginBottom: '1rem' }}>
            Your current status:{' '}
            <strong style={{ color: isMember ? '#15803d' : '#374151' }}>
              {isMember ? 'SCALA Member' : 'Non-member'}
            </strong>
          </p>

          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
            <button
              type="button"
              className={`c-btn${isMember ? '' : ' c-btn--outline'}`}
              disabled={memberLoading || isMember}
              onClick={() => handleMembershipChange(true)}
            >
              {memberLoading && !isMember ? (
                <>
                  <span className="c-spinner c-spinner--sm c-spinner--white" aria-hidden="true" />
                  Saving…
                </>
              ) : (
                'Set as Member'
              )}
            </button>
            <button
              type="button"
              className={`c-btn${!isMember ? '' : ' c-btn--outline'}`}
              disabled={memberLoading || !isMember}
              onClick={() => handleMembershipChange(false)}
            >
              {memberLoading && isMember ? (
                <>
                  <span className="c-spinner c-spinner--sm c-spinner--white" aria-hidden="true" />
                  Saving…
                </>
              ) : (
                'Set as Non-member'
              )}
            </button>
          </div>
        </div>

        <div style={{ marginTop: '32px' }}>
          <Link to="/contestant/dashboard" className="c-back-btn">← Back to Dashboard</Link>
        </div>

      </main>
    </div>
  );
}
